import type { LinkProps } from '@tanstack/react-router';
import { notFoundOrUnknownKey } from '@/api/errors';
import type { ProcessCategory, ProcessStatus } from '@bliks-io/contracts';
import type { SelectOption } from './user-utils';

export type ProcessDetailOrigin = 'all-processes' | 'process-map' | 'process-chains';

export const PROCESS_DETAIL_BACK_ROUTES: Record<ProcessDetailOrigin, LinkProps['to']> = {
  'all-processes': '/$slug/processes/all-processes',
  'process-map': '/$slug/processes/process-map',
  'process-chains': '/$slug/processes/process-chains'
};

export function processDetailState(from: ProcessDetailOrigin) {
  return { from };
}

export const PROCESS_CATEGORY_COLOR_SCHEME: Record<
  ProcessCategory,
  'success' | 'warning' | 'gray'
> = {
  core: 'success',
  management: 'warning',
  support: 'gray'
} as const;

export const PROCESS_STATUS_COLOR_SCHEME: Record<
  ProcessStatus,
  'success' | 'warning' | 'blue' | 'neutral'
> = {
  not_started: 'neutral',
  in_recording: 'warning',
  recording_complete: 'blue',
  completed: 'success'
} as const;

/**
 * The solid-fill counterpart of {@link PROCESS_STATUS_COLOR_SCHEME}, for the
 * surfaces that paint a status rather than badge it (map dot, progress bar).
 */
export const PROCESS_STATUS_FILL_CLASS: Record<ProcessStatus, string> = {
  not_started: 'bg-gray-secondary',
  in_recording: 'bg-yellow-primary',
  recording_complete: 'bg-blue-primary',
  completed: 'bg-green-primary'
} as const;

/** Lifecycle order, so a status column sorts by progress and not alphabetically. */
export const PROCESS_STATUS_ORDER: ProcessStatus[] = [
  'not_started',
  'in_recording',
  'recording_complete',
  'completed'
];

/**
 * One literal key per branch: `check:translations` resolves keys statically and
 * cannot follow a template key.
 */
export function processStatusLabel(status: ProcessStatus, t: (key: string) => string): string {
  switch (status) {
    case 'not_started':
      return t('processes.statusNotStarted');
    case 'in_recording':
      return t('processes.statusInProgress');
    case 'recording_complete':
      return t('processes.statusRecordingComplete');
    case 'completed':
      return t('processes.statusCompleted');
  }
}

export function getProcessCategoryOptions(t: (key: string) => string): SelectOption[] {
  return [
    {
      value: 'management' satisfies ProcessCategory,
      label: t('processChains.categories.management')
    },
    { value: 'core' satisfies ProcessCategory, label: t('processChains.categories.core') },
    { value: 'support' satisfies ProcessCategory, label: t('processChains.categories.support') }
  ];
}

export const processErrorMessageKey = (error: unknown) =>
  notFoundOrUnknownKey(error, 'processes.notFound');

export function filterProcessesBySearch<
  T extends { name: string; department?: { name: string } | null }
>(processes: T[], search: string): T[] {
  if (!search) return processes;
  const q = search.toLowerCase();
  return processes.filter(
    (p) => p.name.toLowerCase().includes(q) || p.department?.name?.toLowerCase().includes(q)
  );
}
